'use client';

import React from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@core/ui/primitives/context-menu';
import { Kbd } from '@core/ui/primitives/kbd';
import {
  LuArrowDown,
  LuArrowLeft,
  LuArrowLeftRight,
  LuArrowRight,
  LuArrowUp,
  LuCornerDownLeft,
  LuCornerDownRight,
  LuFileText,
  LuGitBranch,
  LuImageDown,
  LuList,
  LuPalette,
  LuFileUp,
  LuDownload,
  LuCopy,
  LuClipboardPaste,
  LuShapes,
  LuSquareDashed,
} from 'react-icons/lu';

import type { MindmapLayoutDirection, MindmapNodeShape } from '@/features/mindmap/core/MindmapTypes';
import type { MindmapNodeData } from '@/features/mindmap/core/MindmapTransform';
import { NODE_STYLE_COLOR_SWATCHES } from './NodeStyleToolbar';

const MENU_ICON_CLASS = 'h-5 w-5 shrink-0 text-muted-foreground';

const SHAPE_GLYPH_CLASS =
  'flex h-5 w-5 items-center justify-center rounded border border-border text-[11px] leading-none';

type StylePatch = Partial<Pick<MindmapNodeData, 'bgColor' | 'borderColor' | 'shape'>>;

const SHAPE_OPTIONS: Array<{ id: MindmapNodeShape; label: string; glyph: string }> = [
  { id: 'rounded', label: 'Rounded', glyph: '◼︎' },
  { id: 'rectangle', label: 'Rectangle', glyph: '▭' },
  { id: 'pill', label: 'Pill', glyph: '⬭' },
  { id: 'underline', label: 'Underline', glyph: '▁' },
];

const DIRECTION_OPTIONS: Array<{ id: MindmapLayoutDirection; label: string; icon: React.ReactNode }> = [
  { id: 'left', label: 'Left', icon: <LuArrowLeft className={MENU_ICON_CLASS} aria-hidden /> },
  { id: 'right', label: 'Right', icon: <LuArrowRight className={MENU_ICON_CLASS} aria-hidden /> },
  { id: 'up', label: 'Up', icon: <LuArrowUp className={MENU_ICON_CLASS} aria-hidden /> },
  { id: 'down', label: 'Down', icon: <LuArrowDown className={MENU_ICON_CLASS} aria-hidden /> },
];

const COLOR_OPTIONS = NODE_STYLE_COLOR_SWATCHES;

export interface MindmapNodeContextMenuProps {
  isRoot: boolean;
  onOpen?: () => void;
  onCreateSiblingAfter?: () => void;
  onCreateSiblingBefore?: () => void;
  onCreateChildFirst?: () => void;
  onCreateChildLast?: () => void;
  onChangeBgColor?: (color: string) => void;
  onChangeBorderColor?: (color: string) => void;
  onChangeShape?: (shape: MindmapNodeShape) => void;
  onChangeDirection?: (direction: MindmapLayoutDirection) => void;
  onPreviewStyle?: (patch: StylePatch) => void;
  currentBgColor?: string;
  currentBorderColor?: string;
  currentShape?: MindmapNodeShape;
  // Clipboard operations
  onCopyNode?: () => void;
  onPasteNode?: () => void;
  onPasteSubtree?: () => void;
  onPasteStyle?: () => void;
  canPaste?: boolean;
  children: React.ReactNode;
}

export function MindmapNodeContextMenu({
  isRoot,
  onOpen,
  onCreateSiblingAfter,
  onCreateSiblingBefore,
  onCreateChildFirst,
  onCreateChildLast,
  onChangeBgColor,
  onChangeBorderColor,
  onChangeShape,
  onChangeDirection,
  onPreviewStyle,
  currentBgColor,
  currentBorderColor,
  currentShape,
  onCopyNode,
  onPasteNode,
  onPasteSubtree,
  onPasteStyle,
  canPaste,
  children,
}: MindmapNodeContextMenuProps) {
  const previewBgColor = (value: string | undefined) => {
    if (!onPreviewStyle) return;
    onPreviewStyle({ bgColor: value });
  };

  const previewBorderColor = (value: string | undefined) => {
    if (!onPreviewStyle) return;
    onPreviewStyle({ borderColor: value });
  };

  const previewShape = (value: MindmapNodeShape | undefined) => {
    if (!onPreviewStyle) return;
    onPreviewStyle({ shape: value });
  };

  const resetAllStylePreview = () => {
    if (!onPreviewStyle) return;
    onPreviewStyle({
      bgColor: currentBgColor,
      borderColor: currentBorderColor,
      shape: currentShape,
    });
  };

  const resetBgPreview = () => previewBgColor(currentBgColor);
  const resetBorderPreview = () => previewBorderColor(currentBorderColor);
  const resetShapePreview = () => previewShape(currentShape);

  return (
    <ContextMenu
      onOpenChange={(open: boolean) => {
        if (open) {
          onOpen?.();
        } else {
          resetAllStylePreview();
        }
      }}>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent
                onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}>
        <ContextMenuLabel inset>Tree</ContextMenuLabel>
        {!isRoot && (
          <>
            <ContextMenuItem
              className='flex items-center gap-2'
              onSelect={() => {
                onCreateSiblingAfter?.();
              }}>
              <LuGitBranch className={MENU_ICON_CLASS} aria-hidden />
              <span>Add sibling after</span>
              <ContextMenuShortcut>
                <Kbd keys={['Enter']} size='sm' />
              </ContextMenuShortcut>
            </ContextMenuItem>
            <ContextMenuItem
              className='flex items-center gap-2'
              onSelect={() => {
                onCreateSiblingBefore?.();
              }}>
              <LuArrowUp className={MENU_ICON_CLASS} aria-hidden />
              <span>Add sibling before</span>
              <ContextMenuShortcut>
                <Kbd keys={['Shift', 'Enter']} size='sm' />
              </ContextMenuShortcut>
            </ContextMenuItem>
          </>
        )}
        <ContextMenuItem
          disabled={!onCreateChildLast}
          className='flex items-center gap-2'
          onSelect={() => {
            onCreateChildLast?.();
          }}>
          <LuCornerDownRight className={MENU_ICON_CLASS} aria-hidden />
          <span>Add child last</span>
          <ContextMenuShortcut>
            <Kbd keys={['Tab']} size='sm' />
          </ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuItem
          disabled={!onCreateChildFirst}
          className='flex items-center gap-2'
          onSelect={() => {
            onCreateChildFirst?.();
          }}>
          <LuCornerDownLeft className={MENU_ICON_CLASS} aria-hidden />
          <span>Add child first</span>
          <ContextMenuShortcut>
            <Kbd keys={['Shift', 'Tab']} size='sm' />
          </ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuLabel inset>Clipboard</ContextMenuLabel>
        <ContextMenuItem
          className='flex items-center gap-2'
          onSelect={() => {
            onCopyNode?.();
          }}>
          <LuCopy className={MENU_ICON_CLASS} aria-hidden />
          <span>Copy</span>
          <ContextMenuShortcut>
            <Kbd keys={['meta', 'C']} size='sm' />
          </ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuItem
          className='flex items-center gap-2'
          disabled={!canPaste}
          onSelect={() => {
            onPasteNode?.();
          }}>
          <LuClipboardPaste className={MENU_ICON_CLASS} aria-hidden />
          <span>Paste node</span>
          <ContextMenuShortcut>
            <Kbd keys={['meta', 'V']} size='sm' />
          </ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuItem
          className='flex items-center gap-2'
          disabled={!canPaste}
          onSelect={() => {
            onPasteSubtree?.();
          }}>
          <LuClipboardPaste className={MENU_ICON_CLASS} aria-hidden />
          <span>Paste subtree</span>
          <ContextMenuShortcut>
            <Kbd keys={['meta', 'Shift', 'V']} size='sm' />
          </ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuItem
          className='flex items-center gap-2'
          disabled={!canPaste}
          onSelect={(e) => {
            onPasteStyle?.();
          }}>
          <LuClipboardPaste className={MENU_ICON_CLASS} aria-hidden />
          <span>Paste style</span>
          <ContextMenuShortcut>
            <Kbd keys={['meta', 'Alt', 'V']} size='sm' />
          </ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuLabel inset>Styles</ContextMenuLabel>
        {isRoot && (
          <ContextMenuSub>
            <ContextMenuSubTrigger className='flex items-center gap-2'>
              <LuArrowLeftRight className={MENU_ICON_CLASS} aria-hidden />
              Direction
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              {DIRECTION_OPTIONS.map((option) => (
                <ContextMenuItem
                  key={option.id}
                  className='flex items-center gap-2'
                  onSelect={() => {
                    onChangeDirection?.(option.id);
                  }}>
                  {option.icon}
                  <span className='text-sm'>{option.label}</span>
                </ContextMenuItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>
        )}
        <ContextMenuSub>
          <ContextMenuSubTrigger className='flex items-center gap-2'>
            <LuPalette className={MENU_ICON_CLASS} aria-hidden />
            Background
          </ContextMenuSubTrigger>
          <ContextMenuSubContent
            className='grid grid-cols-4 gap-1 p-2'
            onPointerLeave={() => resetBgPreview()}
            onEscapeKeyDown={() => resetBgPreview()}
            onFocusOutside={() => resetBgPreview()}>
            {COLOR_OPTIONS.map((color) => (
              <ContextMenuItem
                key={`bg-${color}`}
                aria-label={`Background ${color}`}
                onSelect={() => {
                  onChangeBgColor?.(color);
                }}
                className='flex h-7 w-7 items-center justify-center rounded-md p-0'
                onPointerEnter={() => previewBgColor(color)}
                onFocus={() => previewBgColor(color)}>
                <span
                  className='h-4 w-4 rounded-full border border-border'
                  style={{ backgroundColor: color }}
                  aria-hidden
                />
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSub>
          <ContextMenuSubTrigger className='flex items-center gap-2'>
            <LuShapes className={MENU_ICON_CLASS} aria-hidden />
            Shape
          </ContextMenuSubTrigger>
          <ContextMenuSubContent
            onPointerLeave={() => resetShapePreview()}
            onEscapeKeyDown={() => resetShapePreview()}
            onFocusOutside={() => resetShapePreview()}>
            {SHAPE_OPTIONS.map((shapeOption) => (
              <ContextMenuItem
                key={shapeOption.id}
                className='flex items-center gap-2'
                onSelect={() => {
                  onChangeShape?.(shapeOption.id);
                }}
                onPointerEnter={() => previewShape(shapeOption.id)}
                onFocus={() => previewShape(shapeOption.id)}>
                <span className={SHAPE_GLYPH_CLASS} aria-hidden>
                  {shapeOption.glyph}
                </span>
                <span>{shapeOption.label}</span>
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSub>
          <ContextMenuSubTrigger className='flex items-center gap-2'>
            <LuSquareDashed className={MENU_ICON_CLASS} aria-hidden />
            Border
          </ContextMenuSubTrigger>
          <ContextMenuSubContent
            className='grid grid-cols-4 gap-1 p-2'
            onPointerLeave={() => resetBorderPreview()}
            onEscapeKeyDown={() => resetBorderPreview()}
            onFocusOutside={() => resetBorderPreview()}>
            {COLOR_OPTIONS.map((color) => (
              <ContextMenuItem
                key={`border-${color}`}
                aria-label={`Border ${color}`}
                onSelect={() => {
                  onChangeBorderColor?.(color);
                }}
                className='flex h-7 w-7 items-center justify-center rounded-md p-0'
                onPointerEnter={() => previewBorderColor(color)}
                onFocus={() => previewBorderColor(color)}>
                <span
                  className='h-4 w-4 rounded-full border border-border'
                  style={{ backgroundColor: color }}
                  aria-hidden
                />
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
      </ContextMenuContent>
    </ContextMenu>
  );
}

export interface MindmapCanvasContextMenuProps {
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  onExportAsPng: () => void;
  onExportAsMindmapText: () => void;
  onExportAsList: () => void;
  onImportMindmap: () => void;
  onDownloadMindmap: () => void;
}

export function MindmapCanvasContextMenu({
  children,
  onOpenChange,
  onExportAsPng,
  onExportAsMindmapText,
  onExportAsList,
  onImportMindmap,
  onDownloadMindmap,
}: MindmapCanvasContextMenuProps) {
  return (
    <ContextMenu
      onOpenChange={(open) => {
        onOpenChange?.(open);
      }}>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent
                onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}>
        <ContextMenuItem
          className='flex items-center gap-2'
          onSelect={() => {
            onImportMindmap();
          }}>
          <LuFileUp className={MENU_ICON_CLASS} aria-hidden />
          <span>Import mindmap</span>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuLabel inset>Export</ContextMenuLabel>
        <ContextMenuItem
          className='flex items-center gap-2'
          onSelect={() => {
            onExportAsPng();
          }}>
          <LuImageDown className={MENU_ICON_CLASS} aria-hidden />
          <span>Export as PNG</span>
        </ContextMenuItem>
        <ContextMenuItem
          className='flex items-center gap-2'
          onSelect={() => {
            onDownloadMindmap();
          }}>
          <LuDownload className={MENU_ICON_CLASS} aria-hidden />
          <span>Download .mmp</span>
        </ContextMenuItem>
        <ContextMenuItem
          className='flex items-center gap-2'
          onSelect={() => {
            onExportAsMindmapText();
          }}>
          <LuFileText className={MENU_ICON_CLASS} aria-hidden />
          <span>Copy as Mindmap</span>
        </ContextMenuItem>
        <ContextMenuItem
          className='flex items-center gap-2'
          onSelect={() => {
            onExportAsList();
          }}>
          <LuList className={MENU_ICON_CLASS} aria-hidden />
          <span>Copy as list</span>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
